// =============================================================
// 🧮 metricsService.js — Cálculo REAL das métricas da Dashboard
// =============================================================
//
// O que este módulo faz:
// - Converte timestamps
// - Agrupa atendimentos
// - Calcula KPIs (ativos, pendentes, finalizados)
// - Calcula tempos médios
// - Calcula sessões por dia/hora
// - Calcula distribuição por avatar
// - Calcula taxa de resolução automática (se autoResolved existir)
//
// É usado pelo hook: useDashboardMetrics()
//
// =============================================================

// -------------------------------------------------------------
// 🔧 Conversão segura de Timestamp → Date
// -------------------------------------------------------------
function tsToDate(ts) {
  try {
    if (!ts) return null;
    if (typeof ts.toDate === "function") return ts.toDate();
    if (ts.seconds) return new Date(ts.seconds * 1000);
    return null;
  } catch {
    return null;
  }
}

function isSameDay(dateA, dateB) {
  if (!dateA || !dateB) return false;

  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

// -------------------------------------------------------------
// 🧠 Função principal
// -------------------------------------------------------------
export function computeDashboardMetrics(sessions) {
  const safeSessions = Array.isArray(sessions) ? sessions : [];

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // KPIs
  let activeSessions = 0;
  let pendingSessions = 0;
  let finishedToday = 0;

  // Tempos médios
  let responseTimeSumMs = 0;
  let responseTimeCount = 0;

  let sessionDurationSumMs = 0;
  let sessionDurationCount = 0;

  // Agrupamentos
  const byDay = {};
  const byHour = {};
  const byAvatar = {};

  // Resolução automática
  let autoResolvedTotal = 0;
  let autoResolvedCount = 0;

  // -------------------------------------------------------------
  // 🧩 PROCESSAMENTO PRINCIPAL
  // -------------------------------------------------------------
  safeSessions.forEach((session) => {
    const status = session.status;
    const createdAt = tsToDate(session.createdAt);
    const iniciouEm = tsToDate(session.iniciouEm);
    const finalizadoEm = tsToDate(session.finalizadoEm);

    // -----------------------------
    // STATUS PRINCIPAIS
    // -----------------------------
    if (status === "aceita") activeSessions++;
    if (status === "pendente") pendingSessions++;

    if (status === "finalizada" && finalizadoEm) {
      if (isSameDay(finalizadoEm, today)) {
        finishedToday++;
      }

      // duração da sessão
      if (iniciouEm) {
        const durMs = finalizadoEm - iniciouEm;
        if (durMs > 0) {
          sessionDurationSumMs += durMs;
          sessionDurationCount++;
        }
      }
    }

    // -----------------------------
    // TEMPO DE RESPOSTA (createdAt → iniciouEm)
    // -----------------------------
    if (createdAt && iniciouEm) {
      const diff = iniciouEm - createdAt;
      if (diff > 0) {
        responseTimeSumMs += diff;
        responseTimeCount++;
      }
    }

    // -----------------------------
    // SESSÕES POR DIA
    // -----------------------------
    if (createdAt) {
      const key = createdAt.toISOString().slice(0, 10); // yyyy-mm-dd
      byDay[key] = (byDay[key] || 0) + 1;
    }

    // -----------------------------
    // SESSÕES POR HORA
    // -----------------------------
    if (createdAt) {
      const hour = createdAt.getHours();
      const label = hour.toString().padStart(2, "0") + "h";
      byHour[label] = (byHour[label] || 0) + 1;
    }

    // -----------------------------
    // DISTRIBUIÇÃO POR AVATAR
    // -----------------------------
    const avatar = session.avatarName || "Desconhecido";
    byAvatar[avatar] = (byAvatar[avatar] || 0) + 1;

    // -----------------------------
    // RESOLUÇÃO AUTOMÁTICA (se campo existir)
    // -----------------------------
    // Se no futuro você quiser calcular isso com base nas mensagens,
    // basta popular session.autoResolved antes de chamar computeDashboardMetrics.
    if (typeof session.autoResolved === "boolean") {
      autoResolvedTotal++;
      if (session.autoResolved) autoResolvedCount++;
    }
  });

  // -------------------------------------------------------------
  // 📊 MÉTRICAS FINAIS
  // -------------------------------------------------------------
  const avgResponseTimeMinutes =
    responseTimeCount > 0
      ? responseTimeSumMs / responseTimeCount / 60000
      : 0;

  const avgSessionDurationMinutes =
    sessionDurationCount > 0
      ? sessionDurationSumMs / sessionDurationCount / 60000
      : 0;

  const sessionsByDay = Object.entries(byDay)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => (a.date > b.date ? 1 : -1));

  const sessionsByHour = Object.entries(byHour)
    .map(([hour, count]) => ({ hour, count }))
    .sort((a, b) => a.hour.localeCompare(b.hour));

  const avatarDistribution = Object.entries(byAvatar).map(
    ([avatar, count]) => ({ avatar, count })
  );

  const autoResolutionRate =
    autoResolvedTotal > 0 ? autoResolvedCount / autoResolvedTotal : 0;

  // -------------------------------------------------------------
  // RETORNO FINAL
  // -------------------------------------------------------------
  return {
    metrics: {
      activeSessions,
      pendingSessions,
      finishedToday,
      avgResponseTimeMinutes,
      avgSessionDurationMinutes,
    },
    sessionsByDay,
    sessionsByHour,
    avatarDistribution,
    autoResolutionRate,
  };
}
